import React, { useState } from "react";
import { buttonStyle, buttonBackgroundColor } from "./Style";

interface DeleteConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  itemName: string;
  itemType?: string; // 예: "학생", "반", "사용자" 등
  message?: string; // 커스텀 메시지
}

/**
 * 삭제 확인 다이얼로그 컴포넌트
 * 
 * 삭제 전 확인을 위해 사용자에게 아이템 이름을 정확히 입력하도록 요구합니다.
 * 
 * @example
 * ```tsx
 * const [dialogOpen, setDialogOpen] = useState(false);
 * 
 * <DeleteConfirmDialog
 *   isOpen={dialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   onConfirm={async () => {
 *     await apiClient.deleteStudent(id);
 *     setDialogOpen(false);
 *   }}
 *   itemName={student.name}
 *   itemType="학생"
 * />
 * ```
 */
export default function DeleteConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  itemType = "항목",
  message,
}: DeleteConfirmDialogProps) {
  const [confirmName, setConfirmName] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (confirmName !== itemName) return;
    
    try {
      setLoading(true);
      await onConfirm();
      setConfirmName("");
    } catch (error) {
      console.error("삭제 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return; // 처리 중에는 닫기 방지
    setConfirmName("");
    onClose();
  };

  const defaultMessage = (
    <>
      <span style={{ color: "#d33", fontWeight: "bold" }}>{itemName}</span> {itemType}을(를) 삭제하려면, 아래에 이름을 정확히 입력하세요.<br />
      <span style={{ color: "#888", fontSize: "13px" }}>삭제는 되돌릴 수 없습니다.</span>
    </>
  );

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.4)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 4px 24px #3338",
          padding: "32px 38px 28px 38px",
          minWidth: "340px",
          maxWidth: "500px",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()} // 팝업 내부 클릭시 닫힘 방지
      >
        <h3 style={{ margin: 0, fontWeight: "bold", fontSize: "18px", marginBottom: "18px" }}>
          {itemType} 삭제 확인
        </h3>
        
        <div style={{ marginBottom: "16px", fontSize: "16px" }}>
          {message || defaultMessage}
        </div>

        <input
          type="text"
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          placeholder={`${itemType} 이름 입력`}
          disabled={loading}
          style={{
            width: "100%",
            borderRadius: 6,
            border: "1px solid #d0d7de",
            padding: "8px 10px",
            fontSize: "14px",
            textAlign: "left",
            background: loading ? "#f6f8fa" : "#fff",
            color: "#24292f",
            boxSizing: "border-box",
            marginBottom: "20px",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && confirmName === itemName && !loading) {
              handleConfirm();
            }
          }}
        />

        <div style={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              ...buttonStyle,
              ...buttonBackgroundColor.cancel,
              opacity: loading ? 0.5 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmName !== itemName || loading}
            style={{
              ...buttonStyle,
              background:
                confirmName === itemName && !loading
                  ? buttonBackgroundColor.delete.background
                  : buttonBackgroundColor.cancel.background,
              cursor: confirmName === itemName && !loading ? "pointer" : "not-allowed",
              opacity: confirmName === itemName && !loading ? 1 : 0.5,
            }}
          >
            {loading ? "삭제 중..." : "삭제"}
          </button>
        </div>
      </div>
    </div>
  );
}
